import React, { useEffect, useState } from "react";
import { Map, Marker } from "pigeon-maps";
import { ChevronDown, Check } from "lucide-react";

const cyan = "14,165,233";

/* ── Section card ── */
const SectionCard = ({ title, children }) => (
  <div
    className="domain-section-card relative rounded-lg border border-surfaceBorder bg-transparent p-4 overflow-y-auto h-[350px]"
    onMouseEnter={(e) => {
      e.currentTarget.style.background = `radial-gradient(ellipse 80% 60% at 50% 0%, rgba(${cyan},0.06) 0%, transparent 100%)`;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = "transparent";
    }}
  >
    {/* Title — matches crypto's label style */}
    <div className="flex items-center gap-2 mb-4">
      <span
        className="text-[10px] font-semibold tracking-widest uppercase"
        style={{ color: `rgba(${cyan},0.6)` }}
      >
        {title}
      </span>
      <div
        className="flex-1 h-px"
        style={{ background: `rgba(${cyan},0.1)` }}
      />
    </div>

    <div className="space-y-0 divide-y divide-surfaceBorder">{children}</div>
  </div>
);

/* ── Key/value row ── */
const DataRow = ({ label, value, valueBadge }) => (
  <div className="flex justify-between items-center py-2 gap-x-4">
    <span className="text-[10px] tracking-widest uppercase text-mutedText/70 font-medium shrink-0">
      {label}
    </span>
    {valueBadge || (
      <span className="text-[11px] tracking-wide text-mainText text-right truncate ">
        {value}
      </span>
    )}
  </div>
);

/* ── Status badge ── */
const StatusBadge = ({ status, type }) => {
  const base =
    "inline-flex items-center gap-1 text-[10px] tracking-wide rounded-full px-2.5 py-0.5 font-medium";

  if (type === "service") {
    if (status === "Open")
      return (
        <span
          className={`${base} bg-emerald-500/15 text-emerald-400 border border-emerald-500/20`}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" />
          {status}
        </span>
      );
    if (status === "Closed")
      return (
        <span className={`${base} bg-mainButtonBg text-mutedText`}>
          {status}
        </span>
      );
    return (
      <span
        className={`${base} bg-red-500/15 text-red-400 border border-red-500/20`}
      >
        <span className="w-1.5 h-1.5 rounded-full bg-red-400 inline-block" />
        {status}
      </span>
    );
  }

  if (type === "blacklist") {
    if (status === "Not listed")
      return (
        <span className={`${base} bg-mainButtonBg text-mutedText`}>
          <Check className="w-3 h-3" strokeWidth={2.5} />
          {status}
        </span>
      );
    return (
      <span
        className={`${base} bg-red-500/15 text-red-400 border border-red-500/20`}
      >
        {status}
      </span>
    );
  }

  return (
    <span className={`${base} bg-mainButtonBg text-mutedText`}>{status}</span>
  );
};

/* ── Inner component ── */
const DomainsIpTileInner = (props) => {
  const [isIpDropdownOpen, setIsIpDropdownOpen] = useState(false);
  const ipAddresses = props.companyDomainInfo.map((item) => item.ip_address);
  const [currentIp, setCurrentIp] = useState(ipAddresses[0]);
  const [selectedIp, setSelectedIp] = useState(
    Object.entries(props.companyDomainInfo[0]).map(([key, value]) => ({
      key,
      value,
    })),
  );

  const [infoData, setInfoData] = useState([]);
  const [ownerData, setOwnerData] = useState([]);
  const [locationData, setLocationData] = useState([]);
  const [servicesData, setServicesData] = useState([]);
  const [blacklistData, setBlacklistData] = useState([]);
  const [coordinates, setCoordinates] = useState(null);

  const toggleDropdownClick = (ip) => {
    try {
      setIsIpDropdownOpen(false);
      setCurrentIp(ip);
      const foundObject = props.companyDomainInfo.find(
        (item) => item.ip_address === ip,
      );
      const arrayOfObjects = Object.entries(foundObject).map(
        ([key, value]) => ({ key, value }),
      );
      setSelectedIp(arrayOfObjects);
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    try {
      selectedIp.forEach((item) => {
        switch (item.key) {
          case "Info":
            setInfoData(item.value || []);
            break;
          case "Owner":
            setOwnerData(item.value || []);
            break;
          case "Location":
            const filteredLocationData = [];
            const seenCoordinates = { latitude: false, longitude: false };
            item.value.forEach((loc) => {
              const typeLower = loc.type.toLowerCase();
              if (typeLower === "latitude" || typeLower === "longitude") {
                if (!seenCoordinates[typeLower]) {
                  filteredLocationData.push(loc);
                  seenCoordinates[typeLower] = true;
                }
              } else {
                filteredLocationData.push(loc);
              }
            });
            setLocationData(filteredLocationData || []);
            const latitude = filteredLocationData.find(
              (loc) => loc.type.toLowerCase() === "latitude",
            );
            const longitude = filteredLocationData.find(
              (loc) => loc.type.toLowerCase() === "longitude",
            );
            if (latitude && longitude) {
              setCoordinates([
                parseFloat(latitude.data),
                parseFloat(longitude.data),
              ]);
            } else {
              setCoordinates(null);
            }
            break;
          case "Services":
            setServicesData(item.value || []);
            break;
          case "Blacklist":
            setBlacklistData(item.value || []);
            break;
          default:
            break;
        }
      });
    } catch (e) {
      console.log(e);
    }
  }, [selectedIp]);

  const nameBadges = (
    selectedIp.find((item) => item.key === "name_value")?.value || ""
  )
    .split("\n")
    .filter((str) => str.length > 0);

  return (
    <>
      <style>{`
        @keyframes domain-section-pulse {
          0%, 100% { box-shadow: 0 0 0 1px rgba(${cyan},0.06), 0 0 12px 2px rgba(${cyan},0.08); }
          50%       { box-shadow: 0 0 0 1px rgba(${cyan},0.12), 0 0 16px 3px rgba(${cyan},0.13); }
        }
        .domain-section-card { border: 1px solid transparent; transition: all 200ms ease-out; }
        .domain-section-card:hover { animation: domain-section-pulse 5s ease-in-out infinite; }
        .ip-chip-btn { transition: border-color 150ms ease, background 150ms ease; }
        .ip-chip-btn:hover { border-color: rgba(${cyan},0.35) !important; background: rgba(${cyan},0.08) !important; }
      `}</style>

      <div className="rounded-lg border border-surfaceBorder bg-surface/80 backdrop-blur-md shadow-login-panel overflow-hidden">
        <div className="flex flex-col text-mainText">
          {/* ── IP selector + name badges ── */}
          <div className="flex flex-row px-4 pt-4 pb-3 items-center gap-3 border-b border-surfaceBorder flex-wrap">
            <div className="relative inline-block text-left z-20 shrink-0">
              <button
                onClick={() => setIsIpDropdownOpen(!isIpDropdownOpen)}
                className="ip-chip-btn inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-[12px]  tracking-wide text-mainText cursor-pointer outline-none"
                style={{
                  background: `rgba(${cyan},0.05)`,
                  borderColor: `rgba(${cyan},0.18)`,
                }}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full shrink-0"
                  style={{
                    background: `rgba(${cyan},0.8)`,
                    boxShadow: `0 0 6px rgba(${cyan},0.5)`,
                  }}
                />
                {currentIp}
                <ChevronDown
                  className={[
                    "h-3.5 w-3.5 shrink-0 transition-transform duration-200 text-mutedText",
                    isIpDropdownOpen ? "rotate-180" : "",
                  ].join(" ")}
                  strokeWidth={2}
                />
              </button>

              {isIpDropdownOpen && (
                <div className="absolute left-0 mt-2 w-56 origin-top-left rounded-lg border border-surfaceBorder bg-surface/80 backdrop-blur-md shadow-login-panel overflow-hidden z-30">
                  <div className="py-1">
                    {ipAddresses.map((item, index) => {
                      try {
                        return (
                          <button
                            key={index}
                            className="flex items-center gap-2 w-full px-4 py-2 text-[12px] tracking-wide text-mutedText hover:text-mainText transition-colors duration-150 text-left cursor-pointer"
                            style={{
                              background:
                                item === currentIp
                                  ? `rgba(${cyan},0.06)`
                                  : "transparent",
                            }}
                            onClick={() => toggleDropdownClick(item)}
                          >
                            {item === currentIp && (
                              <span
                                className="w-1 h-1 rounded-full shrink-0"
                                style={{ background: `rgba(${cyan},0.8)` }}
                              />
                            )}
                            <span className={item === currentIp ? "" : "ml-3"}>
                              {item}
                            </span>
                          </button>
                        );
                      } catch (err) {
                        console.error("Failed to render ip button", err);
                        return null;
                      }
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Name badges */}
            <div className="flex flex-wrap gap-1.5 min-w-0">
              {nameBadges.map((word, idx) => {
                try {
                  return (
                    <span
                      key={idx}
                      className="inline-block bg-mainButtonBg text-mutedText text-[10px] tracking-wide rounded-full px-2.5 py-0.5 border border-surfaceBorder truncate max-w-[180px]"
                      title={word}
                    >
                      {word}
                    </span>
                  );
                } catch (err) {
                  console.error("Failed to render name value badge", err);
                  return null;
                }
              })}
            </div>
          </div>

          {/* ── Data grid ── */}
          <div className="grid grid-cols-3 gap-4 p-4 overflow-y-auto">
            {infoData.length > 0 && (
              <SectionCard title="Info">
                {infoData.map((infoItem, index) => {
                  try {
                    return (
                      <DataRow
                        key={index}
                        label={infoItem.type}
                        value={infoItem.data}
                      />
                    );
                  } catch (err) {
                    console.error("Failed to render ip info", err);
                    return null;
                  }
                })}
              </SectionCard>
            )}

            {servicesData.length > 0 && (
              <SectionCard title="Services">
                {servicesData.map((service, index) => {
                  try {
                    return (
                      <DataRow
                        key={index}
                        label={service.type}
                        valueBadge={
                          <StatusBadge status={service.data} type="service" />
                        }
                      />
                    );
                  } catch (err) {
                    console.error("Failed to render ip service", err);
                    return null;
                  }
                })}
              </SectionCard>
            )}

            {ownerData.length > 0 && (
              <SectionCard title="Owner">
                {ownerData.map((ownerItem, index) => {
                  try {
                    const isUnknown = ownerItem.data === "false";
                    return (
                      <DataRow
                        key={index}
                        label={ownerItem.type}
                        value={isUnknown ? undefined : ownerItem.data}
                        valueBadge={
                          isUnknown ? (
                            <StatusBadge status="Unknown" type="owner" />
                          ) : undefined
                        }
                      />
                    );
                  } catch (err) {
                    console.error("Failed to render ip owner", err);
                    return null;
                  }
                })}
              </SectionCard>
            )}

            {locationData.length > 0 && (
              <SectionCard title="Location">
                {locationData.map((locationItem, index) => {
                  try {
                    return (
                      <DataRow
                        key={index}
                        label={locationItem.type}
                        value={locationItem.data}
                      />
                    );
                  } catch (err) {
                    console.error("Failed to render ip location", err);
                    return null;
                  }
                })}
              </SectionCard>
            )}

            {coordinates && (
              <div
                className="domain-section-card flex justify-center items-center h-[350px] rounded-lg border border-surfaceBorder overflow-hidden"
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = `radial-gradient(ellipse 80% 60% at 50% 0%, rgba(${cyan},0.06) 0%, transparent 100%)`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                }}
              >
                <Map
                  defaultCenter={coordinates}
                  defaultZoom={10}
                  className="h-full w-full"
                >
                  <Marker anchor={coordinates} />
                </Map>
              </div>
            )}

            {blacklistData.length > 0 && (
              <SectionCard title="Blacklists">
                {blacklistData.map((blacklistItem, index) => {
                  try {
                    return (
                      <DataRow
                        key={index}
                        label={blacklistItem.type}
                        valueBadge={
                          <StatusBadge
                            status={blacklistItem.data}
                            type="blacklist"
                          />
                        }
                      />
                    );
                  } catch (err) {
                    console.error("Failed to render ip blacklist", err);
                    return null;
                  }
                })}
              </SectionCard>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

/* ── Outer wrapper ── */
const DomainsIpTile = (props) => {
  if (!props.companyDomainInfo?.length) return null;
  return <DomainsIpTileInner {...props} />;
};

export default DomainsIpTile;
